import type { Sim } from '@/types'

import { palette } from '@/constants/palette'

const GM = 1
const SEMI_MAJOR = 1
const ECCENTRICITY = 0.6
const SEMI_MINOR = SEMI_MAJOR * Math.sqrt(1 - ECCENTRICITY * ECCENTRICITY)
const FOCUS = SEMI_MAJOR * ECCENTRICITY
const PERIOD = 2 * Math.PI * Math.sqrt(SEMI_MAJOR ** 3 / GM)
const WEDGE = PERIOD / 7
const TIME_SCALE = 0.85
const STEP = 0.002

export const createOrbit = (): Sim => {
    let x = SEMI_MAJOR * (1 - ECCENTRICITY)
    let y = 0
    let vx = 0
    let vy = Math.sqrt((GM * (1 + ECCENTRICITY)) / (SEMI_MAJOR * (1 - ECCENTRICITY)))
    let time = 0
    let accumulator = 0
    const history: { t: number, x: number, y: number }[] = [{ t: 0, x, y }]

    const acceleration = (px: number, py: number): [number, number] => {
        const distanceSquared = px * px + py * py
        const factor = -GM / (distanceSquared * Math.sqrt(distanceSquared))

        return [px * factor, py * factor]
    }

    const integrate = (h: number) => {
        const [ax, ay] = acceleration(x, y)

        vx += ax * h * 0.5
        vy += ay * h * 0.5
        x += vx * h
        y += vy * h

        const [bx, by] = acceleration(x, y)

        vx += bx * h * 0.5
        vy += by * h * 0.5
        time += h
    }

    const drawArrow = (ctx: CanvasRenderingContext2D, fromX: number, fromY: number, toX: number, toY: number) => {
        const angle = Math.atan2(toY - fromY, toX - fromX)

        ctx.beginPath()
        ctx.moveTo(fromX, fromY)
        ctx.lineTo(toX, toY)
        ctx.stroke()

        ctx.beginPath()
        ctx.moveTo(toX, toY)
        ctx.lineTo(toX - Math.cos(angle - 0.45) * 7, toY - Math.sin(angle - 0.45) * 7)
        ctx.moveTo(toX, toY)
        ctx.lineTo(toX - Math.cos(angle + 0.45) * 7, toY - Math.sin(angle + 0.45) * 7)
        ctx.stroke()
    }

    return {
        resize: () => {},

        update: (dt: number) => {
            accumulator += dt * TIME_SCALE

            while (accumulator >= STEP) {
                integrate(STEP)
                accumulator -= STEP
            }

            history.push({ t: time, x, y })

            while (history.length > 2 && history[0].t < time - WEDGE) history.shift()
        },

        draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => {
            const scale = Math.min((width * 0.82) / (2 * SEMI_MAJOR), (height * 0.74) / (2 * SEMI_MINOR))
            const centerX = width / 2
            const centerY = height / 2
            const starX = centerX + FOCUS * scale
            const starY = centerY
            const toCanvasX = (value: number) => starX + value * scale
            const toCanvasY = (value: number) => starY - value * scale

            ctx.save()
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'

            ctx.strokeStyle = palette.hairline
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.moveTo(centerX - SEMI_MAJOR * scale - 12, centerY)
            ctx.lineTo(centerX + SEMI_MAJOR * scale + 12, centerY)
            ctx.stroke()

            ctx.setLineDash([3, 5])
            ctx.strokeStyle = palette.faint
            ctx.beginPath()
            ctx.ellipse(centerX, centerY, SEMI_MAJOR * scale, SEMI_MINOR * scale, 0, 0, Math.PI * 2)
            ctx.stroke()
            ctx.setLineDash([])

            if (history.length > 1) {
                ctx.beginPath()
                ctx.moveTo(starX, starY)
                history.forEach(point => ctx.lineTo(toCanvasX(point.x), toCanvasY(point.y)))
                ctx.closePath()
                ctx.fillStyle = 'rgba(47, 73, 255, 0.12)'
                ctx.fill()
                ctx.strokeStyle = 'rgba(47, 73, 255, 0.45)'
                ctx.lineWidth = 1
                ctx.stroke()
            }

            const glow = ctx.createRadialGradient(starX, starY, 0, starX, starY, 34)

            glow.addColorStop(0, 'rgba(255, 91, 46, 0.35)')
            glow.addColorStop(1, 'rgba(255, 91, 46, 0)')
            ctx.fillStyle = glow
            ctx.beginPath()
            ctx.arc(starX, starY, 34, 0, Math.PI * 2)
            ctx.fill()

            ctx.fillStyle = palette.signal
            ctx.beginPath()
            ctx.arc(starX, starY, 9, 0, Math.PI * 2)
            ctx.fill()

            const planetX = toCanvasX(x)
            const planetY = toCanvasY(y)
            const speed = Math.sqrt(vx * vx + vy * vy)
            const length = 12 + speed * 16

            ctx.strokeStyle = palette.ink
            ctx.lineWidth = 1.5
            drawArrow(ctx, planetX, planetY, planetX + (vx / speed) * length, planetY - (vy / speed) * length)

            ctx.fillStyle = palette.brand
            ctx.strokeStyle = palette.paper
            ctx.lineWidth = 2.5
            ctx.beginPath()
            ctx.arc(planetX, planetY, 7, 0, Math.PI * 2)
            ctx.fill()
            ctx.stroke()

            ctx.font = '500 10px ui-monospace, SFMono-Regular, Menlo, monospace'
            ctx.fillStyle = palette.muted
            ctx.textAlign = 'center'
            ctx.fillText('perihelion', centerX + SEMI_MAJOR * scale - 4, centerY + 22)
            ctx.fillText('aphelion', centerX - SEMI_MAJOR * scale + 4, centerY + 22)

            ctx.restore()
        }
    }
}
